/**
 * Phase 4 Task 12 — supervisor CallMonitor.
 *
 * Renders the active-call list with listen / whisper / barge buttons.
 *
 * Deviation from spec: the action buttons hit the REST endpoints that
 * Task 8 already shipped (`POST /supervisor/{action}/:callId`) rather
 * than emitting `supervisor:action` socket events. The REST path is
 * authoritative — it carries idempotency-key handling, audit logging,
 * and per-action permission gating — and the gateway socket surface
 * has no equivalent today.
 */
import type { JSX } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import type { SupervisorLiveCall } from '@kc3/types'
import { useAuthStore } from '../../store/authStore.js'
import { useUiStore } from '../../store/uiStore.js'

/**
 * ITEM 102 — the actions this component can invoke.
 *
 * Was 'listen' | 'whisper' | 'barge' — three of the twelve the executor handles
 * and the REST layer registers, so six working backends had no control at all.
 *
 * The TRANSFERS are deliberately absent: blind_transfer and attended_transfer
 * require a `targetEndpoint` body (see requiresTargetEndpoint in
 * api/routes/supervisor/actions.ts), which a one-click button cannot supply.
 * A button that always 400s is worse than no button; they need a target picker,
 * which is item 98's work.
 */
type Action =
  | 'listen'
  | 'whisper'
  | 'coach'
  | 'barge'
  | 'park'
  | 'unpark'
  | 'conference'

interface Props {
  calls: SupervisorLiveCall[]
  /**
   * Override the REST base URL — defaults to `/api`. Tests inject a
   * mock fetch and don't care about the URL prefix.
   */
  apiBaseUrl?: string
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

export function CallMonitor({
  calls,
  apiBaseUrl = '/api',
}: Props): JSX.Element {
  const csrfToken = useAuthStore((s) => s.csrfToken)
  const addNotification = useUiStore((s) => s.addNotification)

  async function invokeAction(action: Action, callId: string): Promise<void> {
    try {
      const res = await fetch(`${apiBaseUrl}/supervisor/${action}/${callId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: '{}',
      })
      if (!res.ok) {
        addNotification(`Supervisor ${action} failed (${res.status})`, 'error')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      addNotification(`Supervisor ${action} failed: ${msg}`, 'error')
    }
  }

  const actionsTemplate = (call: SupervisorLiveCall): JSX.Element => (
    <div className="flex gap-1">
      <Button
        icon="pi pi-eye"
        size="small"
        text
        aria-label={`Listen to ${call.callId}`}
        tooltip="Listen"
        onClick={() => void invokeAction('listen', call.callId)}
      />
      <Button
        icon="pi pi-megaphone"
        size="small"
        text
        aria-label={`Whisper to ${call.callId}`}
        tooltip="Whisper"
        onClick={() => void invokeAction('whisper', call.callId)}
      />
      <Button
        icon="pi pi-comments"
        size="small"
        text
        aria-label={`Coach ${call.callId}`}
        tooltip="Coach (agent hears you, caller does not)"
        onClick={() => void invokeAction('coach', call.callId)}
      />
      <Button
        icon="pi pi-users"
        size="small"
        text
        severity="warning"
        aria-label={`Barge into ${call.callId}`}
        tooltip="Barge"
        onClick={() => void invokeAction('barge', call.callId)}
      />
      <Button
        icon="pi pi-pause"
        size="small"
        text
        aria-label={`Park ${call.callId}`}
        tooltip="Park"
        onClick={() => void invokeAction('park', call.callId)}
      />
      <Button
        icon="pi pi-play"
        size="small"
        text
        aria-label={`Unpark ${call.callId}`}
        tooltip="Unpark (returns the caller to their original bridge)"
        onClick={() => void invokeAction('unpark', call.callId)}
      />
      <Button
        icon="pi pi-user-plus"
        size="small"
        text
        aria-label={`Conference ${call.callId}`}
        tooltip="Conference"
        onClick={() => void invokeAction('conference', call.callId)}
      />
    </div>
  )

  const durationTemplate = (call: SupervisorLiveCall): string =>
    formatDuration(call.durationSec)

  return (
    <DataTable
      value={calls}
      size="small"
      stripedRows
      emptyMessage="No active calls."
      data-testid="call-monitor"
    >
      <Column field="agentName" header="Agent" />
      <Column field="callerCLI" header="Caller" />
      <Column field="queueId" header="Queue" />
      <Column header="Duration" body={durationTemplate} />
      <Column header="Actions" body={actionsTemplate} />
    </DataTable>
  )
}
